import { Outlet, useLocation } from "react-router";
import { useCallback, useMemo } from "react";
import * as am5 from "@amcharts/amcharts5";
import { Layout, Menu, MenuProps } from "antd";
import { Toast, Button } from "antd-mobile";
import {
  DoubleLeftOutlined,
  DoubleRightOutlined,
  QuestionCircleOutlined,
  ReadOutlined,
  SettingOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { FilterOutline, AddOutline } from "antd-mobile-icons";
import classNames from "classnames";

// Local imports
import styles from "./styles.module.scss";
import { Header as HeaderMobile } from "../../components/LayoutComponents/Header/index.mobile";
import { MobileLeftMenu } from "../../components/LayoutComponents/LeftMenu/index.mobile";
import { Footer } from "../../components/LayoutComponents/Footer/index.mobile";
import { HeaderWeb } from "../../components/LayoutComponents/HeaderWeb";
import { useHook } from "./useHook";
import { amchartLicenseKey } from "../../config";
import {
  premiumTabs,
  settingsTabs,
  tabs,
  tabsWeb,
  healthAppTabs,
} from "./tabs";
import {
  amplifyProvider,
  authController,
  circadiaEngineProvider,
  clinicController,
  localStorageProvider,
  notificationController,
  patientController,
  pushNotificationProvider,
  timeProvider,
  electronEngineProvider,
  summaryController,
  webIntercomProvider,
  homeHealthController,
  analyticsController,
  smsController,
} from "../../AppController";
import { useAuth } from "../../hooks/auth";
import { useRouterHook } from "../../hooks/useRouterHook";
import { AppErrorBoundary } from "../../components/CustomComponents/PageErrorBoundry";
import { ErrorComponent } from "../../components/CustomComponents/PageErrorBoundry/ErrorComponent";
import { NetworkBar } from "../../components/CustomComponents/NetworkBar";
import { appNotification } from "../../util/appNotification";
import { useScrollTop } from "../../hooks/useScrollTop";
import { useScreenTimeMetrics } from "../../hooks/useScreenTimeMetrics";
import { routes } from "../../router/mobile/routes";
import { ScreenLoaderWhite } from "../../components/CustomComponents/ScreenLoaderWhite";
import { useFCMTokenUpdate } from "../../hooks/fcmTokenListener";
import { sendZapierTrigger } from "../../util/zapierMain";
import { isMobile, isTablet, isWeb } from "../../util/layout";
import { TMobileRouterPath, useAppNavigate } from "../../hooks/router";
import logo from "../../assets/logo.svg";
import { useIntercom } from "../../hooks/useIntercom";
import { SearchPatientModal } from "../../components/CustomComponents/SearchPatientModal";
import { useSearchHook } from "./useSearchHook";
import { useScreenTimeout } from "../../hooks/useScreenTimeout";
import { useSiderAutoCollapse } from "../../hooks/useSiderAutoCollapse";
import { useMobileTopBottomColor } from "../../hooks/useMobileTopBottomColor";
import { isPIN } from "../../util/pin";

if (amchartLicenseKey) {
  am5.addLicense(amchartLicenseKey);
}

const { Sider, Content } = Layout;

type MenuItem = Required<MenuProps>["items"][number];

let lastMainPath = "";

const createMenuItems = (
  items: typeof tabsWeb | typeof premiumTabs | typeof settingsTabs,
  collapsed: boolean,
  setRouteActive: (route: string) => void,
): MenuItem[] => {
  return items.map((item) => ({
    label: item.title,
    icon: item.icon,
    key: item.key,
    onClick: () => setRouteActive(item.key),
    className: classNames(
      styles.menuItem,
      !collapsed && styles.menuItemCollapsed,
    ),
  }));
};

const MobileLayout: FC = () => {
  useMobileTopBottomColor({
    circadiaEngineProvider: circadiaEngineProvider,
  });
  useFCMTokenUpdate({
    electronEngineProvider: electronEngineProvider,
    pushNotificationProvider: pushNotificationProvider,
  });
  const { populateAppData } = useRouterHook({
    amplifyProvider: amplifyProvider,
    authController: authController,
    clinicController: clinicController,
    localStorageProvider: localStorageProvider,
    notificationController: notificationController,
    timeProvider: timeProvider,
    appNotification: appNotification,
    patientController: patientController,
    circadiaEngineProvider,
    summaryController,
    homeHealthController,
    isPIN,
    smsController: smsController,
  });
  const location = useLocation();

  const {
    isSearchPatientModalOpen,
    onSearchPatientModalOpen,
    onSearchPatientModalClose,

    searchValue,
    onChange,
    response,

    isInWatchlist,
    addToWatchlist,
    removeFromWatchlist,
    isAddingList,
  } = useSearchHook();
  const {
    menuOpen,
    user,
    userClinic,
    toggleMenu,
    onClickSearch,
    showHeaderBar,
    showFooter,
    onRightIconClick,
    uniquePageKey,
    confirmLogout,
    onCancel,
    onLogoutClick,
    setConfirmLogout,
    localBetaFeature,
    onLocalBetaFeature,
    footerTapEvent,
    topRightIcon,
    connectionStatus,
    showLoading,
    onPatientClick,
    refreshSummary,
    onSetupClick,
  } = useHook({
    analyticsController,
    location,
    clinicController: clinicController,
    populateAppData,
    summaryController,
    localStorageProvider,
  });

  const {
    menuHover,
    collapsed,
    handleCollapse,
    handleMenuHover,
    onSiderItemClick,
  } = useSiderAutoCollapse();

  useScreenTimeMetrics({ analyticsController });

  // useSummary(10 * 60 * 1000);

  useScreenTimeout({
    localStorageProvider,
    location,
    refreshSummary,
    isPIN,
  });
  const { onLogout } = useAuth({
    authController: authController,
    localStorageProvider: localStorageProvider,
    pushNotificationProvider,
    populateAppData,
    analyticsController,
    appNotification,
    circadiaEngineProvider,
    sendZapierTrigger,
  });

  const pathname = location.pathname;
  const buttonIcon = (
    <FilterOutline className={styles.actionButtonIconFilter} />
  );

  useScrollTop();

  const navigate = useAppNavigate();
  const setRouteActive = useCallback(
    (route: string) => {
      navigate(route);
    },
    [navigate],
  );

  const items = useMemo(
    () => createMenuItems(tabsWeb, collapsed, setRouteActive),
    [tabsWeb, collapsed, setRouteActive],
  );
  const premiumItems = useMemo(
    () => createMenuItems(premiumTabs, collapsed, setRouteActive),
    [premiumTabs, collapsed, setRouteActive],
  );

  const handleLogout = useCallback(() => {
    setConfirmLogout(false);
    setTimeout(() => {
      onLogout();
    }, 100);
  }, [onLogout]);

  lastMainPath = tabsWeb
    .map((val) => val.key)
    .includes(location.pathname as TMobileRouterPath)
    ? location.pathname
    : lastMainPath;

  lastMainPath = premiumTabs
    .map((val) => val.key)
    .includes(location.pathname as TMobileRouterPath)
    ? location.pathname
    : lastMainPath;

  lastMainPath = settingsTabs
    .map((val) => val.key)
    .includes(location.pathname as TMobileRouterPath)
    ? location.pathname
    : lastMainPath;

  const { handleToggle } = useIntercom({
    webIntercomProvider: webIntercomProvider,
    circadiaEngineProvider: circadiaEngineProvider,
    analyticsController: analyticsController,
  });

  // Other full-screen routes without header/footer
  if (
    location.pathname.includes("summary") ||
    location.pathname.includes("chime") ||
    location.pathname.includes("patient-profile") ||
    location.pathname.includes("user-info") ||
    location.pathname.includes("bed-exit-settings")
  ) {
    return (
      <div className={styles.app}>
        <div className={styles.body}>
          <AppErrorBoundary
            FallbackComponent={({ error, resetErrorBoundary }) => (
              <ErrorComponent
                error={error}
                resetErrorBoundary={resetErrorBoundary}
              />
            )}
            resetKeys={[uniquePageKey]}
          >
            <Outlet />
          </AppErrorBoundary>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.app}>
      <NetworkBar connectionStatus={connectionStatus} toast={Toast} />
      {/* {!isMobile() && (
        <Layout>
          <Sider
            width={200}
            theme="light"
            collapsible
            collapsed={collapsed}
            onCollapse={handleCollapse}
            className={styles.sider}
            onClick={() => {
              if (collapsed) {
                onSiderItemClick();
              }
            }}
            id="sider"
            collapsedWidth={80}
            onMouseEnter={isWeb() ? () => handleMenuHover(true) : undefined}
            onMouseLeave={isWeb() ? () => handleMenuHover(false) : undefined}
            onTouchStart={!isWeb() ? () => handleMenuHover(true) : undefined}
            onTouchEnd={!isWeb() ? () => handleMenuHover(false) : undefined}
          >
            <div
              className={classNames(
                collapsed ? styles.logoContainerCollapsed : styles.logoContainer
              )}
            >
              {menuHover && collapsed ? (
                <DoubleRightOutlined
                  className={styles.icon}
                  onClick={() => handleCollapse(!collapsed)}
                />
              ) : (
                <>
                  <img alt="err" src={logo} width="24" />
                  {!collapsed && (
                    <DoubleLeftOutlined
                      className={styles.icon}
                      onClick={() => handleCollapse(!collapsed)}
                    />
                  )}
                </>
              )}
            </div>
            <div className={styles.menuSection} style={{ height: "100%" }}>
              <div>
                <Menu
                  defaultSelectedKeys={[lastMainPath]}
                  selectedKeys={[lastMainPath]}
                  mode="inline"
                  items={items}
                  className={styles.menu}
                />

                <div className={styles.menuDivider}>
                  {!collapsed && (
                    <span className={styles.menuDividerText}>AI Agents</span>
                  )}
                  <span className={styles.menuDividerLine} />
                </div>
                <Menu
                  defaultSelectedKeys={[lastMainPath]}
                  selectedKeys={[lastMainPath]}
                  mode="inline"
                  items={premiumItems}
                  className={styles.menu}
                />
              </div>
              <span className={styles.settingsMenuDividerLine} />
              <Menu
                defaultSelectedKeys={[lastMainPath]}
                selectedKeys={[lastMainPath]}
                className={classNames(styles.menu, styles.menuSupport)}
                mode="inline"
                items={[
                  {
                    label: "Settings",
                    icon: <SettingOutlined className={styles.footerIcon} />,
                    key: routes.settings.path,
                    onClick: () => {
                      if (isWeb() || isTablet()) {
                        navigate(routes.settings.path);
                      } else {
                        toggleMenu();
                      }
                    },
                    className: classNames(
                      styles.menuItem,
                      !collapsed && styles.menuItemCollapsed
                    ),
                  },
                  {
                    label: "Knowledge Hub",
                    icon: <ReadOutlined className={styles.footerIcon} />,
                    key: "knowledge-hub",
                    onClick: () => {
                      window.open("https://help.circadia.health", "_blank");
                    },
                    className: classNames(
                      styles.menuItem,
                      !collapsed && styles.menuItemCollapsed
                    ),
                  },
                  {
                    label: "Help",
                    icon: (
                      <QuestionCircleOutlined className={styles.footerIcon} />
                    ),
                    key: "help-docs",
                    onClick: () => {
                      handleToggle();
                    },
                    className: classNames(
                      styles.menuItem,
                      !collapsed && styles.menuItemCollapsed
                    ),
                  },
                ]}
              />
            </div>
          </Sider>
          <Layout>
            <Content
              className={classNames(styles.content, {
                [styles.attachedTop]: shouldAttachToTop,
              })}
            >
              <div id="body">
                <div className={styles.headerContainer}>
                  <HeaderWeb onClickSearch={onSearchPatientModalOpen} />
                </div>
                <AppErrorBoundary
                  FallbackComponent={({ error, resetErrorBoundary }) => (
                    <ErrorComponent
                      error={error}
                      resetErrorBoundary={resetErrorBoundary}
                      connectionStatus={connectionStatus}
                    />
                  )}
                  resetKeys={[uniquePageKey]}
                >
                  {showLoading ? (
                    <ScreenLoaderWhite />
                  ) : (
                    <Outlet />
                  )}
                </AppErrorBoundary>
              </div>
            </Content>
          </Layout>
        </Layout>
      )} */}
      {/* {isMobile() && ( */}
      <>
        <div className={styles.body} id="body">
          {showHeaderBar() && (
            <HeaderMobile
              onClickSearch={onClickSearch}
              rightIcon={
                topRightIcon !== undefined &&
                false && (
                  <div className="headerRightButtonCon">
                    <Button
                      fill="none"
                      onClick={onRightIconClick}
                      data-test-id='"button-filter'
                    >
                      {buttonIcon}
                    </Button>
                  </div>
                )
              }
              leftIcon={
                <span id="menu-icon">
                  {/* <MenuOutlined className="searchIcon" onClick={toggleMenu} /> */}
                  <UserOutlined className="searchIcon" onClick={toggleMenu} />
                </span>
              }
            />
          )}
          <AppErrorBoundary
            FallbackComponent={({ error, resetErrorBoundary }) => (
              <ErrorComponent
                error={error}
                resetErrorBoundary={resetErrorBoundary}
                connectionStatus={connectionStatus}
              />
            )}
            resetKeys={[uniquePageKey]}
          >
            {showLoading ? (
              <ScreenLoaderWhite />
            ) : (
              <Outlet />
            )}
          </AppErrorBoundary>
        </div>
        {showFooter() && (
          <div className={styles.bottom}>
            <Footer tabs={healthAppTabs} tapEvent={footerTapEvent} />
          </div>
        )}
      </>
      {/* )} */}

      <MobileLeftMenu
        user={user}
        userClinic={userClinic}
        open={menuOpen}
        onToggle={toggleMenu}
        onLogout={onLogoutClick}
        confirmLogout={confirmLogout}
        onCancel={onCancel}
        onConfirmLogout={handleLogout}
        analyticsController={analyticsController}
        circadiaEngineProvider={circadiaEngineProvider}
        localBetaFeature={localBetaFeature}
        onLocalBetaFeature={onLocalBetaFeature}
      />
      <SearchPatientModal
        isOpen={isSearchPatientModalOpen}
        onClose={onSearchPatientModalClose}
        searchValue={searchValue}
        onChange={onChange}
        response={response}
        isInWatchlist={isInWatchlist}
        addToWatchlist={addToWatchlist}
        removeFromWatchlist={removeFromWatchlist}
        isAddingList={isAddingList}
        onPatientClick={onPatientClick}
        onSetupClick={onSetupClick}
      />
    </div>
  );
};

export default MobileLayout;
